import { IconName } from '@reward-platform/lift/components/Icon/Icon.types'
import { TabContentProps } from '@reward-platform/lift/components/Tabs'
import { SearchTabQuery, SearchTabQuerySchema } from '~/schemas/searchPageParams'
import {
  ContentfulSearchContent,
  ContentfulSearchContentProduct,
} from '@reward-platform/contentful'

type TabPropsConfig<K = string> = { slug: K; icon: IconName; fallbackTitle: string }
const SEARCH_TAB_PROPS_RECORD: Record<
  ContentfulSearchContentProduct,
  TabPropsConfig<SearchTabQuery>
> = {
  [SearchTabQuerySchema.enum.flights]: {
    slug: SearchTabQuerySchema.enum.flights,
    icon: 'flights-tab',
    fallbackTitle: 'Flights',
  },
  [SearchTabQuerySchema.enum.hotels]: {
    slug: SearchTabQuerySchema.enum.hotels,
    icon: 'rp-hotel',
    fallbackTitle: 'Hotels',
  },
  [SearchTabQuerySchema.enum.carhire]: {
    slug: SearchTabQuerySchema.enum.carhire,
    icon: 'carhire_tab',
    fallbackTitle: 'Car Hire',
  },
  [SearchTabQuerySchema.enum.experiences]: {
    slug: SearchTabQuerySchema.enum.experiences,
    icon: 'experiences-tab',
    fallbackTitle: 'Experiences',
  },
}

const EXTERNAL_LINK_PROPS_RECORD: Record<string, TabPropsConfig> = {
  wineflyer: { slug: 'wineflyer', icon: 'bar', fallbackTitle: 'Wine' },
}

const getFallbackLinkProps = ({ fallbackLink }: ContentfulSearchContent) =>
  fallbackLink
    ? {
        href: fallbackLink,
        variant: 'link',
        isVisible: true,
      }
    : {
        isVisible: false,
      }

const getExternalLinkProps = (searchContent: ContentfulSearchContent) => {
  const externalLinkProps = EXTERNAL_LINK_PROPS_RECORD[searchContent.identifier] || undefined
  if (externalLinkProps) {
    const { slug, icon, fallbackTitle } = externalLinkProps
    const title = searchContent.productLabel ?? fallbackTitle
    return {
      title,
      slug,
      icon,
      ...getFallbackLinkProps(searchContent),
    }
  }
  return null
}

const getTabContentProps = (searchContent: ContentfulSearchContent): TabContentProps | null => {
  const productKey = searchContent.product
  if (!productKey) {
    return null
  }
  const { slug, icon, fallbackTitle } = SEARCH_TAB_PROPS_RECORD[productKey]
  const title = searchContent.productLabel ?? fallbackTitle
  return {
    slug,
    title,
    icon,
    variant: 'tab',
  }
}

export const getTabPropsFromContent = (
  searchContent: ContentfulSearchContent,
  isProductEnabled = true
): TabContentProps | null => {
  const tabProps = getTabContentProps(searchContent)
  const fallbackLinkProps = getFallbackLinkProps(searchContent)

  if (!tabProps) {
    return getExternalLinkProps(searchContent)
  }

  if (isProductEnabled) {
    return { ...tabProps, isVisible: true }
  }

  if (fallbackLinkProps.isVisible) {
    return { ...tabProps, ...fallbackLinkProps }
  }

  return { ...tabProps, isVisible: false }
}
